import { useItemContext } from "@rn-primitives/accordion";
import type { MedicalRequest } from "@spork/s4p-server-sdk";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import { toastError, toastSuccess } from "@/components/AppToast";
import { MedicalIcon } from "@/components/SectionItemIcon";
import {
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { showAlert } from "@/lib/showAlert";
import { log } from "@/logger";
import { useRevokeMedicalRequestMutation } from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

type MedicalRequestHomeCardProps = {
    request: MedicalRequest;
    childLabel?: string;
};

function getErrorMessage(error: unknown, fallback: string): string {
    if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof error.message === "string"
    ) {
        return error.message;
    }
    return fallback;
}

function MedicalRequestTriggerContent({
    request,
    childLabel,
}: {
    request: MedicalRequest;
    childLabel?: string;
}) {
    const { isExpanded } = useItemContext();

    return (
        <View className="min-w-0 flex-row shrink items-center gap-2">
            <MedicalIcon />
            <View className="min-w-0 flex-1 shrink gap-0.5">
                <Text
                    className="min-w-0 flex-1 shrink"
                    numberOfLines={isExpanded ? undefined : 1}
                    ellipsizeMode="tail"
                >
                    {request.note}
                </Text>

                {childLabel ? (
                    <Text variant="muted" numberOfLines={1}>
                        {childLabel}
                    </Text>
                ) : null}
            </View>
        </View>
    );
}

export function MedicalRequestHomeCard({
    request,
    childLabel,
}: MedicalRequestHomeCardProps) {
    const { t } = useTranslation();
    const router = useRouter();
    const [revokeMedicalRequest, { isLoading: isRevoking }] =
        useRevokeMedicalRequestMutation();
    const itemValue = String(request.id);

    function handleEdit() {
        router.push(`/medical-request?requestId=${request.id}`);
    }

    function handleRevoke() {
        showAlert(
            t("medical.revokeConfirmTitle"),
            t("medical.revokeConfirmMessage"),
            [
                {
                    text: t("medical.revokeConfirmKeep"),
                    variant: "outline",
                    testID: TEST_IDS[
                        TEST_IDS.MEDICAL_REQUEST_REVOKE_KEEP_BUTTON
                    ],
                },
                {
                    text: t("medical.revoke"),
                    variant: "destructive",
                    testID: TEST_IDS[
                        TEST_IDS.MEDICAL_REQUEST_REVOKE_CONFIRM_BUTTON
                    ],
                    onPress: async () => {
                        try {
                            await revokeMedicalRequest(request.id).unwrap();
                            toastSuccess(
                                t("medical.revokedTitle"),
                                t("medical.revokedBody"),
                            );
                        } catch (error) {
                            log.error("revokeMedicalRequest failed", error);
                            toastError(
                                t("medical.errorTitle"),
                                getErrorMessage(
                                    error,
                                    t("medical.revokeFailed"),
                                ),
                            );
                        }
                    },
                },
            ],
        );
    }

    const accessibilityLabel = childLabel
        ? t("medical.expandA11y", { name: childLabel })
        : t("medical.expandA11yNoName");

    return (
        <AccordionItem
            value={itemValue}
            testID={TEST_IDS[TEST_IDS.HOME_MEDICAL_REQUEST_CARD]}
        >
            <AccordionTrigger
                className="items-center gap-3 p-3 active:bg-accent"
                accessibilityLabel={accessibilityLabel}
                testID={TEST_IDS[TEST_IDS.HOME_MEDICAL_REQUEST_TOGGLE]}
            >
                <MedicalRequestTriggerContent
                    request={request}
                    childLabel={childLabel}
                />
            </AccordionTrigger>

            <AccordionContent className="border-t border-border p-0 pb-0">
                <View className="gap-3 px-3 pb-3 pt-3">
                    <View className="flex-row gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onPress={handleEdit}
                            disabled={isRevoking}
                            testID={
                                TEST_IDS[
                                    TEST_IDS.HOME_MEDICAL_REQUEST_EDIT_BUTTON
                                ]
                            }
                        >
                            <Text>{t("medical.edit")}</Text>
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onPress={handleRevoke}
                            disabled={isRevoking}
                            testID={
                                TEST_IDS[
                                    TEST_IDS.HOME_MEDICAL_REQUEST_REVOKE_BUTTON
                                ]
                            }
                        >
                            <Text className="text-destructive">
                                {isRevoking
                                    ? t("medical.revoking")
                                    : t("medical.revoke")}
                            </Text>
                        </Button>
                    </View>
                </View>
            </AccordionContent>
        </AccordionItem>
    );
}
